// ============================================
// Computer Desktop + Browser UI
// Replaces BrowserController.cs
// ============================================

import { events, Events } from '../engine/events.js';
import { UITags } from '../engine/constants.js';
import { variableManager } from '../engine/variables.js';
import { L } from '../systems/localization.js';
import { getEffectiveFemGain, getEffectiveSubGain, getEffectiveShameGain } from '../engine/externals.js';

// ── Desktop app metadata ────────────────────────────────────────────────
const DESKTOP_APPS = {
    'browse':       { icon: '\uD83C\uDF10', name: 'Browser',       color: '#5bc0de', img: 'icon_desktop_browser' },
    'browser':      { icon: '\uD83C\uDF10', name: 'Browser',       color: '#5bc0de', img: 'icon_desktop_browser' },
    'stream':       { icon: '\uD83D\uDCF9', name: 'StreamHub',     color: '#e06878', img: 'icon_desktop_streamhub' },
    'streaming':    { icon: '\uD83D\uDCF9', name: 'StreamHub',     color: '#e06878', img: 'icon_desktop_streamhub' },
    'social':       { icon: '\uD83D\uDCF1', name: 'SissySocial',   color: '#e88cb8', img: 'icon_sissysocial' },
    'sissy':        { icon: '\uD83D\uDCF1', name: 'SissySocial',   color: '#e88cb8', img: 'icon_sissysocial' },
    'onlyfems':     { icon: '\uD83D\uDC8E', name: 'OnlyFems',      color: '#c470e0', img: 'icon_onlyfems' },
    'hypno':        { icon: '\uD83C\uDF00', name: 'Hypno',         color: '#a060d0', img: 'icon_desktop_hypno' },
    'marketplace':  { icon: '\uD83D\uDED2', name: 'Marketplace',   color: '#e8c860', img: 'icon_desktop_shop' },
    'shop':         { icon: '\uD83D\uDED2', name: 'Shop',          color: '#e8c860', img: 'icon_desktop_shop' },
    'email':        { icon: '\u2709\uFE0F', name: 'Email',         color: '#5cd89c', img: 'icon_desktop_email' },
    'webcam':       { icon: '\uD83D\uDCF7', name: 'Webcam Setup',  color: '#d86050', img: 'icon_desktop_webcam' },
    'relax':        { icon: '\uD83C\uDFB5', name: 'Relax',         color: '#5888cc', img: 'icon_desktop_relax' },
    'breathe':      { icon: '\uD83E\uDDD8', name: 'Breathe',       color: '#5cd89c', img: 'icon_desktop_breathe' },
    'adult':        { icon: '\uD83D\uDD1E', name: 'Adult Sites',   color: '#d86050', img: 'icon_desktop_adult' },
    'porn':         { icon: '\uD83D\uDD1E', name: 'Videos',        color: '#d86050', img: 'icon_desktop_adult' },
    'close':        { icon: '\u274C',        name: 'Shut Down',     color: '#887ca0', img: 'icon_desktop_shutdown' },
    'shut':         { icon: '\u23FB',        name: 'Shut Down',     color: '#887ca0', img: 'icon_desktop_shutdown' },
    'power':        { icon: '\u23FB',        name: 'Power Off',     color: '#887ca0', img: 'icon_desktop_shutdown' },
    'back':         { icon: '\u2190',        name: 'Back',          color: '#887ca0', img: 'icon_desktop_back' },
};

// ── Website metadata for browser mode ───────────────────────────────────
const WEBSITE_DATA = {
    'streaming':    { icon: '\uD83D\uDCF9', name: 'StreamHub.com',      url: 'streamhub.com',       color: '#e06878' },
    'stream':       { icon: '\uD83D\uDCF9', name: 'StreamHub.com',      url: 'streamhub.com',       color: '#e06878' },
    'social':       { icon: '\uD83D\uDCF1', name: 'SissySocial.net',    url: 'sissysocial.net',     color: '#e88cb8' },
    'sissy':        { icon: '\uD83D\uDCF1', name: 'SissySocial.net',    url: 'sissysocial.net',     color: '#e88cb8' },
    'marketplace':  { icon: '\uD83D\uDED2', name: 'FemShop.com',        url: 'femshop.com',         color: '#e8c860' },
    'shop':         { icon: '\uD83D\uDED2', name: 'FemShop.com',        url: 'femshop.com',         color: '#e8c860' },
    'hypno':        { icon: '\uD83C\uDF00', name: 'SissyHypno.xxx',     url: 'sissyhypno.xxx',      color: '#a060d0' },
    'adult':        { icon: '\uD83D\uDD1E', name: 'AdultVids.xxx',      url: 'adultvids.xxx',       color: '#d86050' },
    'porn':         { icon: '\uD83D\uDD1E', name: 'PinkTube.xxx',       url: 'pinktube.xxx',        color: '#d86050' },
    'email':        { icon: '\u2709\uFE0F', name: 'PinkMail.com',       url: 'pinkmail.com',        color: '#5cd89c' },
    'onlyfems':     { icon: '\uD83D\uDC8E', name: 'OnlyFems.com',       url: 'onlyfems.com',        color: '#c470e0' },
    'webcam':       { icon: '\uD83D\uDCF7', name: 'CamSetup.com',       url: 'camsetup.com',        color: '#d86050' },
    'relax':        { icon: '\uD83C\uDFB5', name: 'ChillBeats.fm',      url: 'chillbeats.fm',       color: '#5888cc' },
    'back':         { icon: '\u2190',        name: 'Back to Desktop',    url: '',                     color: '#887ca0' },
    'close':        { icon: '\u274C',        name: 'Close Browser',      url: '',                     color: '#887ca0' },
};

// ── Star field generation (deterministic seed 42) ───────────────────────
function generateStars(count, seed) {
    const stars = [];
    let s = seed;
    const next = () => {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        return (s % 10000) / 10000;
    };
    for (let i = 0; i < count; i++) {
        stars.push({
            x: next() * 100,
            y: next() * 100,
            size: 0.5 + next() * 1.5,
            opacity: 0.2 + next() * 0.6,
            twinkle: next() > 0.7,
        });
    }
    return stars;
}

const DESKTOP_STARS = generateStars(80, 42);

class ComputerUI {
    constructor() {
        /** @type {HTMLElement|null} */
        this._root = null;

        /** @type {'off'|'desktop'|'browser'} */
        this._mode = 'off';

        /** @type {Function|null} Choice callback */
        this._onChoiceSelected = null;

        this._scope = events.createScope('computer');
        this._bindEvents();
    }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
    }

    // ── Public API ──────────────────────────────────────────────────────

    init() {
        this._root = document.getElementById('overlay-computer');
        if (!this._root) {
            this._root = this._createContainer();
        }
    }

    /**
     * Render choices as desktop app icons or browser cards.
     * @param {Array<{text: string, index: number}>} choices
     * @param {Function} onSelect - Callback with choice index
     */
    renderChoices(choices, onSelect) {
        this._onChoiceSelected = onSelect;

        if (this._mode === 'desktop') {
            this._renderDesktopChoices(choices);
        } else if (this._mode === 'browser') {
            // Check if story rendered images (porn viewing mode)
            // Show a slim browser chrome bar above the story content for immersion,
            // but let the normal story view handle image + text + choices.
            // _forcePornGrid (set by #ui:porn_categories on PornSites) ALWAYS
            // means "render the category-picker grid" — PornSites itself emits
            // no #img:/narration, so any image found in story-content is STALE
            // (carried over from a prior browser knot like StreamingBrowse that
            // didn't get cleared because PornSites also emits zero cards, and
            // the deferred-clear is cancelled for zero-card knots). Without
            // this skip, the porn-categories grid is never shown — the stale
            // streaming image triggers the hide-overlay path and the player
            // sees the StreamHub summary with porn-category choices below it.
            // Foundby:VPS player v0.2.6.2 ("clicked on videos, was shown the
            // stream hub summary again but the video categories are correct").
            const storyContent = document.getElementById('story-content');
            const hasStoryImages = !this._forcePornGrid
                && storyContent
                && storyContent.querySelector('.story-image-wrapper, .story-image');
            if (hasStoryImages) {
                this._root.classList.add('hidden');
                this._injectBrowserChrome(storyContent);
                // 2026-05-21 sibling of hotspot+inline-choice fix in main.js:
                // restore BOTH legacy #choices and the current #choices-inline.
                // Pre-fix only #choices got unhidden, leaving inline choices
                // styled display:none from the prior computer-active toggle.
                const choicesEl = document.getElementById('choices');
                if (choicesEl) choicesEl.classList.remove('hidden');
                const choicesInline = document.getElementById('choices-inline');
                if (choicesInline) choicesInline.style.display = '';
                this._onChoiceSelected = null;
                return;
            }
            this._renderBrowserChoices(choices);
        }
    }

    isActive() {
        return this._mode !== 'off';
    }

    getMode() {
        return this._mode;
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.TAG_UI, (mode) => {
            if (mode === UITags.DESKTOP) {
                this._activateDesktop();
            } else if (mode === UITags.COMPUTER) {
                this._activateBrowser();
            } else if (mode === UITags.PORN_CATEGORIES) {
                this._activatePornBrowser();
            } else {
                this._deactivate();
            }
        });
        // Forced events (SMS [goto:], phone interrupts, accidental orgasm)
        // need to dismiss the computer overlay — otherwise the forced-event
        // story content renders BEHIND the still-active browser/desktop UI
        // and clicks land on the wrong layer. Sibling pattern to wardrobe/
        // phone/shop/streaming/layout listeners. (Audit 2026-05-23.)
        this._scope.on(Events.FORCED_EVENT_STARTED, () => this._deactivate());
    }

    // ── Activation ──────────────────────────────────────────────────────

    _activateDesktop() {
        if (!this._root) this.init();
        this._mode = 'desktop';
        this._root.classList.remove('hidden');
        this._root.innerHTML = '';
        this._buildDesktop();
    }

    _activateBrowser() {
        if (!this._root) this.init();
        this._mode = 'browser';
        this._root.classList.remove('hidden');
        this._root.innerHTML = '';
        this._buildBrowser();
        this._updateTaskbarWindows();
    }

    _activatePornBrowser() {
        if (!this._root) this.init();
        this._mode = 'browser';
        this._forcePornGrid = true;
        this._root.classList.remove('hidden');
        this._root.innerHTML = '';
        this._buildBrowser();
        // Pre-set browser chrome to PinkTube
        const urlEl = this._root.querySelector('#browser-url');
        if (urlEl) urlEl.textContent = 'pinktube.xxx';
        const tabName = this._root.querySelector('.browser-tab-name');
        if (tabName) tabName.textContent = 'PinkTube';
    }

    _updateTaskbarWindows() {
        const container = this._root?.querySelector('#taskbar-windows');
        if (!container) return;
        container.innerHTML = '';
        if (this._mode === 'browser') {
            const btn = document.createElement('button');
            btn.className = 'taskbar-window-btn taskbar-window-active';
            btn.textContent = '\uD83C\uDF10 ' + L.get('computer.browser');
            container.appendChild(btn);
        }
    }

    _deactivate() {
        this._mode = 'off';
        this._forcePornGrid = false;
        if (this._root) {
            this._root.classList.add('hidden');
            this._root.innerHTML = '';
        }
        this._onChoiceSelected = null;
    }

    // ── Desktop Mode ────────────────────────────────────────────────────

    _buildDesktop() {
        const desktop = document.createElement('div');
        desktop.className = 'computer-desktop';

        // Star wallpaper
        const starField = document.createElement('div');
        starField.className = 'desktop-stars';
        for (const star of DESKTOP_STARS) {
            const dot = document.createElement('div');
            dot.className = 'desktop-star' + (star.twinkle ? ' desktop-star-twinkle' : '');
            dot.style.left = `${star.x}%`;
            dot.style.top = `${star.y}%`;
            dot.style.width = `${star.size}px`;
            dot.style.height = `${star.size}px`;
            dot.style.opacity = star.opacity;
            starField.appendChild(dot);
        }
        desktop.appendChild(starField);

        // App icon area (choices go here)
        const iconArea = document.createElement('div');
        iconArea.className = 'desktop-icon-area';
        iconArea.id = 'desktop-icons';
        desktop.appendChild(iconArea);

        // X button (close/power, top-right)
        const closeBtn = document.createElement('button');
        closeBtn.className = 'desktop-close-btn';
        closeBtn.innerHTML = '&times;';
        closeBtn.title = L.get('desktop.shutdown') || 'Shut Down';
        closeBtn.addEventListener('click', () => {
            // Find a "shut down" or "close" or "back" choice
            events.emit('computer:shutdown');
        });
        desktop.appendChild(closeBtn);

        // Taskbar
        const taskbar = document.createElement('div');
        taskbar.className = 'desktop-taskbar';
        const _startLabel = L.get('desktop.start');
        taskbar.innerHTML = `
            <button class="taskbar-start" id="taskbar-start">
                <span class="taskbar-start-icon">\u25B6</span>
                <span>${_startLabel !== 'desktop.start' ? _startLabel : '\u2605 Start'}</span>
            </button>
            <div class="taskbar-divider"></div>
            <div class="taskbar-windows" id="taskbar-windows"></div>
            <div class="taskbar-tray">
                <span class="taskbar-clock" id="taskbar-clock"></span>
            </div>
        `;
        desktop.appendChild(taskbar);

        // Start menu (dynamically populated in _renderDesktopChoices)
        const startMenu = document.createElement('div');
        startMenu.className = 'desktop-start-menu hidden';
        startMenu.id = 'desktop-start-menu';
        desktop.appendChild(startMenu);

        this._root.appendChild(desktop);

        // Start menu toggle
        const startBtn = desktop.querySelector('#taskbar-start');
        startBtn.addEventListener('click', () => {
            startMenu.classList.toggle('hidden');
        });

        // Update clock
        this._updateClock();
    }

    _renderDesktopChoices(choices) {
        const area = this._root.querySelector('#desktop-icons');
        if (!area) return;
        area.innerHTML = '';

        for (const choice of choices) {
            const info = this._getDesktopAppInfo(choice.originalText || choice.text);
            const isDisabled = choice.isEnabled === false;

            const icon = document.createElement('div');
            icon.className = 'desktop-app-icon' + (isDisabled ? ' disabled' : '');
            const iconContent = info.img
                ? `<img src="assets/images/UI/${info.img}.png" style="width:32px;height:32px;border-radius:8px" alt="${info.name}">`
                : info.icon;
            icon.innerHTML = `
                <div class="desktop-app-icon-img" style="background:${info.color}20; border-color:${info.color}40">
                    ${iconContent}
                </div>
                <div class="desktop-app-name">${info.name}</div>
            `;

            // Cost badges
            const badgeHtml = this._buildCostBadges(choice);
            if (badgeHtml) icon.appendChild(badgeHtml);

            // Disabled reason
            if (isDisabled && choice.disabledReason) {
                const reason = document.createElement('div');
                reason.className = 'computer-disabled-reason';
                reason.textContent = choice.disabledReason;
                icon.appendChild(reason);
            }

            if (!isDisabled) {
                icon.addEventListener('click', () => {
                    if (this._onChoiceSelected) {
                        this._onChoiceSelected(choice.index);
                    }
                });

                icon.addEventListener('dblclick', () => {
                    if (this._onChoiceSelected) {
                        this._onChoiceSelected(choice.index);
                    }
                });
            }

            area.appendChild(icon);
        }

        // Populate Start menu from choices (fallback for mobile)
        const startMenu = this._root.querySelector('#desktop-start-menu');
        if (startMenu) {
            startMenu.innerHTML = '';
            for (const choice of choices) {
                const info = this._getDesktopAppInfo(choice.originalText || choice.text);
                const isShutdown = info.name === 'Shut Down' || info.name === 'Power Off';
                const isDisabled = choice.isEnabled === false;

                if (isShutdown) {
                    // Add divider before shutdown
                    const divider = document.createElement('div');
                    divider.className = 'start-menu-divider';
                    startMenu.appendChild(divider);
                }

                const item = document.createElement('div');
                item.className = 'start-menu-item' + (isDisabled ? ' disabled' : '');
                item.innerHTML = `<span>${info.icon}</span> ${info.name}`;

                if (!isDisabled) {
                    item.addEventListener('click', () => {
                        if (isShutdown) {
                            events.emit('computer:shutdown');
                        } else if (this._onChoiceSelected) {
                            this._onChoiceSelected(choice.index);
                        }
                        startMenu.classList.add('hidden');
                    });
                }

                startMenu.appendChild(item);
            }
        }
    }

    _getDesktopAppInfo(choiceText) {
        const lower = choiceText.toLowerCase();
        for (const [key, info] of Object.entries(DESKTOP_APPS)) {
            if (lower.includes(key)) {
                return info;
            }
        }
        return {
            icon: '\uD83D\uDCC1',
            name: choiceText.length > 16 ? choiceText.substring(0, 14) + '...' : choiceText,
            color: '#887ca0',
        };
    }

    // ── Browser Mode ────────────────────────────────────────────────────

    _buildBrowser() {
        const browser = document.createElement('div');
        browser.className = 'computer-browser';

        // Chrome bar
        const chrome = document.createElement('div');
        chrome.className = 'browser-chrome';
        chrome.innerHTML = `
            <div class="browser-tabs">
                <div class="browser-tab active">
                    <span class="browser-tab-icon">\uD83C\uDF10</span>
                    <span class="browser-tab-name">${L.get('computer.new_tab')}</span>
                </div>
            </div>
            <div class="browser-address-bar">
                <span class="browser-address-lock">\uD83D\uDD12</span>
                <span class="browser-address-text" id="browser-url">about:blank</span>
            </div>
            <button class="browser-close-btn" id="browser-close">&times;</button>
        `;
        browser.appendChild(chrome);

        // Content area (choices go here)
        const content = document.createElement('div');
        content.className = 'browser-content';
        content.id = 'browser-content';
        browser.appendChild(content);

        this._root.appendChild(browser);

        // Close button handler
        // F19: null-guard — if the close button is missing (template skew /
        // DOM-rebuild race), log instead of throwing TypeError on .addEventListener.
        const closeBtn = browser.querySelector('#browser-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                events.emit('computer:browserClose');
            });
        } else {
            console.warn('[Computer] #browser-close button not found in browser overlay');
        }
    }

    _renderBrowserChoices(choices) {
        const content = this._root.querySelector('#browser-content');
        if (!content) return;
        content.innerHTML = '';

        // Check if these are porn category choices (forced via #ui:porn_categories or auto-detected)
        const isPornGrid = this._forcePornGrid || choices.some(c => {
            const t = (c.originalText || c.text).toLowerCase();
            return t.includes('straight') || t.includes('amateur') || t.includes('femboy') || t.includes('sissy training') || t.includes('hypno');
        });
        this._forcePornGrid = false; // reset after use

        if (isPornGrid) {
            this._renderPornGrid(content, choices);
            return;
        }

        const grid = document.createElement('div');
        grid.className = 'browser-card-grid';

        for (const choice of choices) {
            const info = this._getWebsiteInfo(choice.originalText || choice.text);
            const isDisabled = choice.isEnabled === false;

            const card = document.createElement('div');
            card.className = 'browser-card' + (isDisabled ? ' disabled' : '');

            card.innerHTML = `
                <div class="browser-card-icon" style="background:${info.color}20; color:${info.color}">
                    ${info.icon}
                </div>
                <div class="browser-card-info">
                    <div class="browser-card-name">${info.name}</div>
                    <div class="browser-card-url">${info.url}</div>
                </div>
            `;

            // Cost badges
            const badgeHtml = this._buildCostBadges(choice);
            if (badgeHtml) card.appendChild(badgeHtml);

            // Disabled reason
            if (isDisabled && choice.disabledReason) {
                const reason = document.createElement('div');
                reason.className = 'computer-disabled-reason';
                reason.textContent = choice.disabledReason;
                card.appendChild(reason);
            }

            if (!isDisabled) {
                card.addEventListener('click', () => {
                    // Update address bar
                    const urlEl = this._root.querySelector('#browser-url');
                    if (urlEl) urlEl.textContent = info.url || info.name;

                    const tabName = this._root.querySelector('.browser-tab-name');
                    if (tabName) tabName.textContent = info.name;

                    if (this._onChoiceSelected) {
                        this._onChoiceSelected(choice.index);
                    }
                });
            }

            grid.appendChild(card);
        }

        content.appendChild(grid);
    }

    _renderPornGrid(content, choices) {
        const urlEl = this._root.querySelector('#browser-url');
        if (urlEl) urlEl.textContent = 'pinktube.xxx';
        const tabName = this._root.querySelector('.browser-tab-name');
        if (tabName) tabName.textContent = 'PinkTube';

        const PORN_CATEGORIES = {
            'straight':       { name: L.get('porn.cat_straight') || 'Straight',       desc: L.get('porn.desc_mainstream') || 'Mainstream',    color: '#5988d8', img: 'porn_straight_missionary' },
            'amateur':        { name: L.get('porn.cat_amateur') || 'Amateur',        desc: L.get('porn.desc_homemade') || 'Homemade',      color: '#d88c58', img: 'amateur_balcony' },
            'femboy':         { name: 'Femboy',         desc: 'Compilation',   color: '#f070b4', img: 'porn_femboy_browsing' },
            'sissy training': { name: L.get('porn.cat_sissy') || 'Sissy Training', desc: L.get('porn.desc_guided') || 'Guided',        color: '#b050b0', img: 'sissy_training_video' },
            'hypno':          { name: L.get('porn.cat_hypno') || 'Hypno',          desc: L.get('porn.desc_conditioning') || 'Conditioning',  color: '#7040d8', img: 'hypno_spiral_thumbnail' },
            'conditioning':   { name: L.get('porn.cat_hypno') || 'Hypno',          desc: L.get('porn.desc_conditioning') || 'Conditioning',  color: '#7040d8', img: 'hypno_spiral_thumbnail' },
        };

        const header = document.createElement('div');
        header.className = 'porn-grid-header';
        header.innerHTML = '<span style="font-size:20px">\uD83D\uDD1E</span> <span>PinkTube.xxx</span>';
        content.appendChild(header);

        const grid = document.createElement('div');
        grid.className = 'porn-grid';

        for (const choice of choices) {
            const lower = (choice.originalText || choice.text).toLowerCase();
            // Skip exit choices
            if (lower.includes('desktop') || lower.includes('power off') || lower.includes('back')) {
                // Render as small "Back" link below grid
                continue;
            }

            let catInfo = null;
            for (const [key, info] of Object.entries(PORN_CATEGORIES)) {
                if (lower.includes(key)) { catInfo = info; break; }
            }
            if (!catInfo) catInfo = { name: choice.text, desc: '', color: '#887ca0', img: null };

            const isDisabled = choice.isEnabled === false;
            const tile = document.createElement('div');
            tile.className = 'porn-tile' + (isDisabled ? ' disabled' : '');
            tile.style.borderColor = catInfo.color + '40';
            tile.innerHTML = `
                <div class="porn-tile-preview" style="background:${catInfo.color}15">
                    ${catInfo.img ? `<img src="assets/images/Computer/${catInfo.img}.png" alt="${catInfo.name}" onerror="this.style.display='none';if(this.parentElement)this.parentElement.innerHTML='\\uD83C\\uDFAC'" style="width:100%;height:100%;object-fit:cover;border-radius:6px">` : '<span style="font-size:36px">\uD83C\uDFAC</span>'}
                </div>
                <div class="porn-tile-info">
                    <div class="porn-tile-name" style="color:${catInfo.color}">${catInfo.name}</div>
                    <div class="porn-tile-desc">${catInfo.desc}</div>
                    <div class="porn-tile-views">${Math.floor(Math.random() * 900 + 100)}K ${L.get('computer.views')}</div>
                </div>
            `;

            // Cost badges
            const badgeHtml = this._buildCostBadges(choice);
            if (badgeHtml) tile.appendChild(badgeHtml);

            if (!isDisabled) {
                tile.addEventListener('click', () => {
                    if (this._onChoiceSelected) this._onChoiceSelected(choice.index);
                });
            }
            grid.appendChild(tile);
        }
        content.appendChild(grid);

        // Back/exit button
        const exitChoice = choices.find(c => {
            const t = (c.originalText || c.text).toLowerCase();
            return t.includes('desktop') || t.includes('power off') || t.includes('back');
        });
        if (exitChoice) {
            const back = document.createElement('button');
            back.className = 'porn-back-btn';
            back.textContent = '\u2190 ' + L.get('computer.back_to_desktop');
            back.addEventListener('click', () => {
                if (this._onChoiceSelected) this._onChoiceSelected(exitChoice.index);
            });
            content.appendChild(back);
        }
    }

    _getWebsiteInfo(choiceText) {
        const lower = choiceText.toLowerCase();
        for (const [key, info] of Object.entries(WEBSITE_DATA)) {
            if (lower.includes(key)) {
                return info;
            }
        }
        return {
            icon: '\uD83C\uDF10',
            name: choiceText.length > 24 ? choiceText.substring(0, 22) + '...' : choiceText,
            url: choiceText.toLowerCase().replace(/\s+/g, '-') + '.com',
            color: '#887ca0',
        };
    }

    // ── Inline Browser Chrome (injected into story-content for immersion) ──

    /**
     * Inject a slim browser chrome bar at the top of story-content.
     * Used when viewing porn: image + text render in normal story view,
     * but a fake browser header adds immersion.
     */
    _injectBrowserChrome(storyContent) {
        // Remove any existing injected chrome
        const old = storyContent.querySelector('.browser-chrome-inline');
        if (old) old.remove();

        const chrome = document.createElement('div');
        chrome.className = 'browser-chrome-inline';
        chrome.innerHTML = `
            <div class="bci-tabs">
                <div class="bci-tab active">
                    <span class="bci-tab-icon">\uD83D\uDD1E</span>
                    <span class="bci-tab-name">PinkTube</span>
                </div>
                <div class="bci-tab-plus">+</div>
            </div>
            <div class="bci-address">
                <span class="bci-lock">\uD83D\uDD12</span>
                <span class="bci-url">pinktube.xxx</span>
            </div>
        `;

        // Insert before the first child (before location header or image)
        storyContent.prepend(chrome);
    }

    // ── Cost Badges ──────────────────────────────────────────────────────

    /**
     * Build a cost badges container for a choice (energy, time, stat previews).
     * @param {object} choice - choice data object with energyCost, timeCost, etc.
     * @returns {HTMLElement|null}
     */
    _buildCostBadges(choice) {
        const badges = [];

        if (choice.energyCost >= 0) {
            const eff = choice.effectiveEnergyCost;
            const showEff = (typeof eff === 'number' && eff !== choice.energyCost);
            const displayCost = showEff ? eff : choice.energyCost;
            badges.push(this._badge(
                'badge-energy' + (showEff ? ' badge-energy-modified' : ''),
                `-${displayCost}E`,
            ));
        }
        if (choice.timeCost > 0) {
            badges.push(this._badge('badge-time', `-${choice.timeCost}T`));
        }
        const story = window._sm?.currentStory;
        // Pacing 2.0: use the RAW tag (femTag/subTag/shameTag) so the accum-aware
        // wrapper is applied exactly once (story.js already made femGain effective
        // for #energy choices; re-applying could floor a real +1 to 0). Fall back
        // to *Gain where no raw tag exists.
        const rawFem = choice.femTag !== undefined && choice.femTag !== -1 ? choice.femTag : choice.femGain;
        const rawSub = choice.subTag !== undefined && choice.subTag !== -1 ? choice.subTag : choice.subGain;
        const rawShame = choice.shameTag !== undefined && choice.shameTag !== -1 ? choice.shameTag : choice.shameGain;
        if (rawFem !== -1 && rawFem !== 0 && rawFem !== undefined) {
            const base = rawFem;
            const eff = base > 0 ? getEffectiveFemGain(base, story) : base;
            if (eff !== 0) {
                const sign = eff > 0 ? '+' : '';
                badges.push(this._badge('badge-fem', `${sign}${eff} Fem`));
            }
        }
        if (rawSub !== -1 && rawSub !== 0 && rawSub !== undefined) {
            const base = rawSub;
            const eff = base > 0 ? getEffectiveSubGain(base, story) : base;
            if (eff !== 0) {
                const sign = eff > 0 ? '+' : '';
                badges.push(this._badge('badge-sub', `${sign}${eff} Sub`));
            }
        }
        if (rawShame !== -1 && rawShame !== 0 && rawShame !== undefined) {
            const base = rawShame;
            const eff = base > 0 ? getEffectiveShameGain(base, story) : base;
            if (eff !== 0) {
                const sign = eff > 0 ? '+' : '';
                badges.push(this._badge('badge-shame', `${sign}${eff} Shame`));
            }
        }

        if (badges.length === 0) return null;

        const container = document.createElement('div');
        container.className = 'computer-badges';
        badges.forEach(b => container.appendChild(b));
        return container;
    }

    _badge(cssClass, text) {
        const span = document.createElement('span');
        span.className = `choice-badge ${cssClass}`;
        span.textContent = text;
        return span;
    }

    // ── Utilities ───────────────────────────────────────────────────────

    _updateClock() {
        const clock = this._root ? this._root.querySelector('#taskbar-clock') : null;
        if (!clock) return;
        const TIME_SLOT_KEYS = ['time.morning', 'time.afternoon', 'time.evening', 'time.night'];
        const TIME_SLOT_FALLBACK = ['Morning', 'Afternoon', 'Evening', 'Night'];
        const day = variableManager ? variableManager.getValue('Day') : null;
        const timeSlot = variableManager ? variableManager.getValue('TimeSlot') : null;
        if (day != null && timeSlot != null) {
            const key = TIME_SLOT_KEYS[timeSlot] || TIME_SLOT_KEYS[0];
            const translated = L.get(key);
            const slotName = translated !== key ? translated : (TIME_SLOT_FALLBACK[timeSlot] || 'Morning');
            const dayKey = L.get('desktop.day');
            const dayLabel = dayKey !== 'desktop.day' ? dayKey.replace('{0}', day) : `Day ${day}`;
            clock.textContent = `${dayLabel} \u2022 ${slotName}`;
        } else {
            const dayKey = L.get('desktop.day');
            const mornKey = L.get('time.morning');
            clock.textContent = `${dayKey !== 'desktop.day' ? dayKey.replace('{0}', 1) : 'Day 1'} \u2022 ${mornKey !== 'time.morning' ? mornKey : 'Morning'}`;
        }
    }

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'overlay-computer';
        el.className = 'computer-overlay hidden';
        const story = document.getElementById('story');
        if (story) {
            story.parentNode.insertBefore(el, story);
        } else {
            document.body.appendChild(el);
        }
        return el;
    }
}

/** @type {ComputerUI} */
export const computerUI = new ComputerUI();
